import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Send, CheckCircle2, MessageSquare, Building2 } from "lucide-react";
import { sendContact, contactErr } from "@/lib/contact";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", topic: "General", message: "", website: "" });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await sendContact(form, "fliboe"); setSent(true); }
    catch (e2) { setErr(contactErr(e2)); } finally { setBusy(false); }
  };

  return (
    <main className="max-w-3xl mx-auto px-5 py-16" data-testid="contact-page">
      <Link to="/" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-emerald-500 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Mail className="w-6 h-6 text-emerald-500" />
        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500">// Get in touch</div>
      </div>
      <h1 className="fl-display text-4xl sm:text-5xl font-extrabold tracking-tighter">Contact us</h1>
      <p className="text-slate-400 mt-3 text-[15px] leading-relaxed">
        Questions, feedback, press, partnerships, or a bug to report — reach the team directly. We read every message
        and reply within one business day.
      </p>

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="fl-card p-6">
          <MessageSquare className="w-5 h-5 text-emerald-500 mb-3" />
          <div className="text-white font-semibold">General inquiries</div>
          <div className="text-slate-400 text-sm mt-1">Broadcaster help, feature requests, feedback.</div>
          <div className="text-emerald-400 text-sm mt-3">Choose "General" in the form below.</div>
        </div>
        <div className="fl-card p-6">
          <Building2 className="w-5 h-5 text-emerald-500 mb-3" />
          <div className="text-white font-semibold">Press &amp; partnerships</div>
          <div className="text-slate-400 text-sm mt-1">Media, advertising, distribution deals.</div>
          <div className="text-emerald-400 text-sm mt-3">Choose "Press / partnership" in the form below.</div>
        </div>
      </div>

      {sent ? (
        <div className="mt-10 fl-card p-6 border-emerald-400/30 bg-emerald-500/5" data-testid="contact-sent">
          <CheckCircle2 className="w-6 h-6 text-emerald-400 mb-3" />
          <div className="text-white font-semibold">Message sent. Thank you!</div>
          <p className="text-slate-400 text-sm mt-1">We'll reply to the email address you gave, usually within one business day.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 fl-card p-6 space-y-4" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Your name</span>
              <input required className="fl-input mt-1 w-full" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Email</span>
              <input required type="email" className="fl-input mt-1 w-full" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Topic</span>
            <select className="fl-input mt-1 w-full" value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} data-testid="contact-topic">
              <option>General</option>
              <option>Broadcaster support</option>
              <option>Bug report</option>
              <option>Press / partnership</option>
              <option>Advertising</option>
              <option>Legal / DMCA</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Message</span>
            <textarea required rows={6} className="fl-input mt-1 w-full" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
          </label>
          <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} aria-hidden="true" />
          {err && <div className="text-red-400 text-sm" data-testid="contact-error">{err}</div>}
          <button type="submit" disabled={busy} className="fl-btn" data-testid="contact-submit">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send message"}
          </button>
        </form>
      )}

      <div className="mt-10 pt-6 border-t border-white/5 text-slate-400 text-sm">
        <p>
          Fliboe FM is operated by its independent owner. For legal notices (DMCA, takedown requests, subpoenas), use the
          form above and choose &quot;Legal / DMCA&quot; as the topic.
        </p>
      </div>
    </main>
  );
}
